import React, {useState, useCallback, Fragment, useRef} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {useSelector} from 'react-redux';
import {labels} from '../../../../../../../util/utils';
import {
	itControlApproachType,
	entities,
	clientSideEventNames,
	promiseAreas
} from '../../../../../../../util/uiconstants';
import Utility from '../../../../../../../util/utilityfunctions';
import {updateITControl} from '../../../../../../../actions/ITControl/itcontrolaction';
import EllipseControl from '../../../../../EllipsesControl/EllipsesControl';
import {useDispatchAction} from '../../../../../CustomHooks/UseDispatchAction';
import {unRelateItControl} from '../../../../../../../actions/ITRisk/itriskactions';
import Ellipses from '../../../../../EllipsesControl/EllipsesPresets';
import GrayFade from '../../../../../GrayFade/GrayFade';
import ClickableIconLabel from '../../../../../ClickableIconLabel/ClickableIconLabel';
import BodySpecialAccordionContentListItemRemove from '../BodySpecialAccordionContentListItemRemove';
import {getAllItControls} from '../../../../../../../actions/ITControl/itcontrolaction';
import ToggleSwitch from '../../../../../BaseComponentWrappers/MotifToggleSwitch';
import {IconoirPlusCircle} from '@ey-xd/motif-icon';

function ISA315ManageITSPListItemExpanded({itProcessId, onCreateUpdate, engagementId, bodyId, openRelateModal}) {
	const dispatch = useDispatchAction();
	const isLoading = useSelector((store) => store.isLoading);
	const [currentItControlId, setCurrentItControlId] = useState(0);
	const [showRemove, setShowRemove] = useState(false);
	const [removeForRisk, setRemoveForRisk] = useState(0);
	const loadingIds = useRef([]);
	const [renderCounter, setRenderCounter] = useState(0);

	const {itProcess, itRisks, itControls} = useSelector((store) => {
		return {
			itProcess: store.bodiesSpecial.itProcesses?.byId[itProcessId],
			itRisks: store.bodiesSpecial.itRisks,
			itControls: store.bodiesSpecial.itControls
		};
	});

	const onCreateNewITGC = useCallback(
		(itRisk) => {
			onCreateUpdate({
				entityId: entities.ItControl,
				parentEntityId: itRisk.id,
				parentEntityName: itRisk.itRiskName,
				itProcessId: itProcessId,
				itProcessName: itProcess.itProcessName
			});
		},
		[entities, itProcessId, itProcess]
	);

	const addExistingItGc = useCallback(
		(itRisk) => {
			openRelateModal({
				bodyId: bodyId,
				entityId: entities.ItControl,
				parentEntity: itRisk,
				selectedItemIds: itRisk.itControls
			});
		},
		[openRelateModal, entities]
	);

	const onCreateNewITSP = useCallback(
		(itRisk) => {
			onCreateUpdate({
				entityId: entities.ItControl,
				parentEntityId: itRisk.id,
				parentEntityName: itRisk.itRiskName,
				itProcessId: itProcessId,
				itProcessName: itProcess.itProcessName,
				itApproachTypeId: itControlApproachType.substantive
			});
		},
		[entities, itProcessId, itProcess]
	);

	const onAddExistingITSP = useCallback(
		(itRisk) => {
			openRelateModal({
				entityId: entities.ItControl,
				parentEntity: itRisk,
				selectedItemIds: itRisk.itControls,
				itApproachTypeId: itControlApproachType.substantive
			});
		},
		[openRelateModal, entities]
	);

	const onEditITRisk = useCallback(
		(itRisk) => {
			onCreateUpdate({
				entityId: entities.ItRisk,
				entityUId: itRisk.id,
				entityName: itRisk?.itRiskName,
				parentEntityId: itProcessId,
				parentEntityName: itProcess?.itProcessName,
				hasNoITGC: itRisk.hasNoITGC
			});
		},
		[entities, itProcessId, itProcess]
	);

	const onEditITControl = useCallback(
		(itRisk, itControl) => {
			onCreateUpdate({
				entityId: entities.ItControl,
				entityUId: itControl.id,
				entityName: itControl?.itControlName,
				itApproachTypeId: itControl.itApproachTypeId,
				parentEntityId: itRisk.id,
				parentEntityName: itRisk?.itRiskName,
				itProcessId: itProcessId,
				itProcessName: itProcess?.itProcessName
			});
		},
		[entities, itProcessId, itProcess]
	);

	const onTestingChange = (itControl, checkedValue) => {
		loadingIds.current.push(itControl.id);
		setRenderCounter(renderCounter + 1);
		const payload = {
			itRiskId: itControl.itRiskId,
			itControlName: itControl.itControlName,
			isEffective: checkedValue === false ? null : itControl.isEffective,
			hasITControlTesting: checkedValue,
			controlDesignEffectivenessTypeId:
				itControl.controlDesignEffectivenessTypeId === 0 ? null : itControl.controlDesignEffectivenessTypeId
		};

		if (checkedValue) {
			payload.taskDescription = labels.itspTaskDescription;
		}

		dispatch(updateITControl(engagementId, itControl.id, payload)).finally(() => {
			loadingIds.current = loadingIds.current.filter((i) => {
				if (i !== itControl.id) return i;
			});
			setRenderCounter(renderCounter + 1);
			setCurrentItControlId(0);
			window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
		});
	};

	const handleOnRemoveITSPClick = (itRisk, controlId) => {
		setRemoveForRisk(itRisk.id);
		loadingIds.current.push(controlId);
		setRenderCounter(renderCounter + 1);
		dispatch(unRelateItControl(engagementId, itRisk.id, controlId))
			.then(() => {
				dispatch(
					getAllItControls(
						engagementId,
						{
							IsUnrelated: true,
							ITApproachTypeId: itControlApproachType.substantive
						},
						{bodyId, metadataKey: 'unrelatedItControls'},
						promiseAreas.ITPROCESS_UNRELATED_ITGC_COUNT
					),
					true
				);
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
			})
			.finally(() => {
				loadingIds.current = loadingIds.current.filter((i) => {
					if (i !== controlId) return i;
				});
				setRenderCounter(renderCounter + 1);
			});
	};

	const handleOnMouseOver = useCallback((itControl, itRisk) => {
		setCurrentItControlId(itControl.id);
		setRemoveForRisk(itRisk.id);
		setShowRemove(true);
	}, []);

	const handleOnMouseLeave = useCallback(() => {
		setShowRemove(false);
		setRemoveForRisk(0);
	}, []);

	return (
		<StyledManageITSPList className="StyledManageITSPList">
			<section className="manageITSPListRiskColumnHeadingWrapper">
				<section className="manageITSPListRiskColumnHeader">{labels.manageITSPItemExpansionMessage}</section>
				<section className="manageITSPListDesignEffectivenessHeader">
					<EllipseControl
						content={labels.itDesignEffectivenessHeader}
						tooltip={labels.itDesignEffectivenessHeader}
						noOfLines={1}
					/>
				</section>
				<section className="manageITSPListTestingHeader">
					<EllipseControl content={labels.itTestingColumnHeader} tooltip={labels.itTestingColumnHeader} noOfLines={1} />
				</section>
			</section>
			<section className="manageITSPListWrapper">
				{itProcess.itRisks.map((itRiskId) => {
					const itRisk = itRisks.byId[itRiskId];
					let itGCs = [];
					let itSPs = [];
					itRisk?.itControls?.forEach((c) => {
						const itControl = itControls.byId[c];
						if (itControl.itApproachTypeId === itControlApproachType.controls) {
							itGCs.push(itControl);
						} else if (itControl.itApproachTypeId === itControlApproachType.substantive) {
							itSPs.push(itControl);
						}
					});

					// Sort itGCs & itSPs by name
					Utility.sortAlphaNumeric(itGCs, 'itControlName');
					Utility.sortAlphaNumeric(itSPs, 'itControlName');

					return (
						<section className="manageITSPListContainer">
							<section className="manageITSPListRiskName">
								<span className="manageITSPListRiskNameText" onClick={() => onEditITRisk(itRisk)}>
									<Ellipses.Single>{itRisk.itRiskName}</Ellipses.Single>
								</span>
							</section>
							<section className="manageITGCListWrapper">
								{itRisk.hasNoITGC ? (
									<section className="NoITGCExist">{labels.noITGCExists}</section>
								) : (
									<Fragment>
										<section className="manageITGCListHeading">{labels.itGCColumnHeader}</section>
										{itGCs.length ? (
											<section className="manageITGCContainer">
												{itGCs.map((c) => (
													<section className="manageITGCListContainer">
														<section className="manageITGCListControlName">
															<span
																className="manageITGCListControlNameText"
																onClick={() => onEditITControl(itRisk, c)}
															>
																{c.itControlName}
															</span>
														</section>
														<section className="manageITGCListControlDesginEffective">
															<Ellipses.Single>
																{c.controlDesignEffectivenessTypeId
																	? labels.controlDesignEffectiveness[c.controlDesignEffectivenessTypeId]?.description
																	: labels.controlDesignEffectiveness[0].description}
															</Ellipses.Single>
														</section>
													</section>
												))}
											</section>
										) : (
											<section className="manageITGCListAllRisks">
												<section className="noITGCPlaceholderMessageFragment">
													{labels.noITGCPlaceholderMessageFragment1}
												</section>
												<section className="noITGCPlaceholderMessage">
													{labels.noITGCPlaceholderMessageFragment2}
												</section>
												<section className="noITGCPlaceholderMessage">
													<Ellipses.Single
														ellipsisClass="it-process-it-risk-risk-item"
														link
														onClick={() => {
															onCreateNewITGC(itRisk);
														}}
													>
														{labels.noITGCPlaceholderMessageFragment3}
													</Ellipses.Single>
												</section>
												<section className="noITGCPlaceholderMessage">
													{labels.noITGCPlaceholderMessageFragment4}
												</section>
												<section className="noITGCPlaceholderMessage">
													<Ellipses.Single
														ellipsisClass="it-process-it-risk-risk-item"
														link
														onClick={() => {
															addExistingItGc(itRisk);
														}}
													>
														{labels.noITGCPlaceholderMessageFragment5}
													</Ellipses.Single>
												</section>
												<section className="noITGCPlaceholderMessage">
													{labels.noITGCPlaceholderMessageFragment6}
												</section>
												<section className="noITGCPlaceholderMessage">
													<Ellipses.Single
														ellipsisClass="it-process-it-risk-risk-item"
														link
														onClick={() => {
															onEditITRisk(itRisk);
														}}
													>
														{labels.noITGCPlaceholderMessageFragment7}
													</Ellipses.Single>
												</section>
												<section className="noITGCPlaceholderMessage">
													{labels.noITGCPlaceholderMessageFragment8}
												</section>
											</section>
										)}
									</Fragment>
								)}
							</section>
							<section className="manageITSPContentListWrapper">
								<section className="manageITSPListHeading">{labels.itSPColumnHeader}</section>
								{itSPs.length ? (
									<section className="manageITSPContainer">
										{itSPs.map((itControl) => (
											<GrayFade
												isGrayFade={isLoading && loadingIds.current.indexOf(itControl.id) !== -1}
												key={itControl.id}
											>
												<section
													onMouseOver={() => {
														handleOnMouseOver(itControl, itRisk);
													}}
													onMouseLeave={handleOnMouseLeave}
													className="manageITSPControlListContainer"
												>
													<section className="manageITSPControlContainer">
														<section className="manageITSPListControlName">
															<span
																className="manageITSPListControlNameText"
																onClick={() => onEditITControl(itRisk, itControl)}
															>
																{itControl.itControlName}
															</span>
														</section>
														<ToggleSwitch
															id={`toggle-${itRisk.itRiskName}-${itControl.id}`}
															checked={itControl.hasITControlTesting}
															hideLabels
															onChange={(e) => onTestingChange(itControl, e.target.checked)}
														/>
														<span className="testing-label">{labels.itTestHeader}</span>
													</section>
													{currentItControlId === itControl.id && itRisk.id === removeForRisk && (
														<BodySpecialAccordionContentListItemRemove
															showRemove={showRemove}
															removeLabel={labels.remove}
															onClick={() => handleOnRemoveITSPClick(itRisk, itControl.id)}
															styleName="manageITSPListControlRemoveLink"
														/>
													)}
												</section>
											</GrayFade>
										))}
									</section>
								) : (
									<section className="manageITSPListAllRisks">
										<section className="noITGCPlaceholderMessage">
											<span className="noITGCPlaceholderMessageSpan">{labels.noITSPPlaceholderMessageFragment1}</span>
											<section className="noITGCPlaceholderMessageSection">
												<Ellipses.Single
													ellipsisClass="it-process-it-risk-risk-item"
													link
													onClick={() => onCreateNewITSP(itRisk)}
												>
													{labels.noITSPPlaceholderMessageFragment2}
												</Ellipses.Single>
											</section>
											<section className="noITGCPlaceholderMessageSection">
												{labels.noITSPPlaceholderMessageFragment3}
											</section>
											<section className="noITGCPlaceholderMessageSection">
												<Ellipses.Single
													ellipsisClass="it-process-it-risk-risk-item"
													link
													onClick={() => onAddExistingITSP(itRisk)}
												>
													{labels.noITSPPlaceholderMessageFragment4}
												</Ellipses.Single>
											</section>
											<section className="noITGCPlaceholderMessageSection">
												{labels.noITSPPlaceholderMessageFragment5}
											</section>
										</section>
									</section>
								)}
							</section>
							<section className="itRiskLinkWrapper">
								<ClickableIconLabel
									styleName="clickable-link"
									text={labels.addNewITSP}
									iconName={IconoirPlusCircle}
									onClick={() => onCreateNewITSP(itRisk)}
								/>
								<ClickableIconLabel
									styleName="clickable-link"
									text={labels.addExistingITSP}
									iconName={IconoirPlusCircle}
									onClick={() => onAddExistingITSP(itRisk)}
								/>
							</section>
						</section>
					);
				})}
			</section>
		</StyledManageITSPList>
	);
}

ISA315ManageITSPListItemExpanded.propTypes = {
	itProcessId: PropTypes.number.isRequired
};

const StyledManageITSPList = styled.section`
	padding: var(--px-20) var(--px-17) var(--px-20) var(--px-46);
	.manageITSPListRiskColumnHeadingWrapper {
		display: flex;
		align-items: center;
		padding: var(--px-10) 0;
		border-bottom: var(--px-1) solid var(--neutrals-200);
		.manageITSPListRiskColumnHeader {
			font-size: var(--px-14);
			font-weight: 300;
			color: var(--neutrals-900);
			width: calc(100% - var(--px-350));
		}
		.manageITSPListDesignEffectivenessHeader {
			font-size: var(--px-12);
			font-weight: 300;
			color: var(--neutrals-900);
			width: var(--px-200);
		}
		.manageITSPListTestingHeader {
			font-size: var(--px-12);
			font-weight: 300;
			color: var(--neutrals-900);
			width: var(--px-130);
		}
	}
	.manageITSPListWrapper {
		padding: var(--px-10) 0 0 0;
		.manageITSPListContainer {
			.manageITSPListRiskName {
				padding: var(--px-10) var(--px-30) var(--px-10) 0;
				border-bottom: var(--px-1) solid var(--neutrals-200);
				.manageITSPListRiskNameText {
					cursor: pointer;
					font-size: var(--px-14);
					font-weight: 600;
					color: var(--blue-600);
					.ellipses {
						width: auto;
					}
					&:hover {
						.ellipses {
							text-decoration: underline;
						}
					}
				}
			}
			.manageITGCListWrapper {
				margin-left: var(--px-10);
				.NoITGCExist {
					font-size: var(--px-14);
					font-weight: 300;
					font-style: italic;
					padding: var(--px-10) 0 var(--px-10) var(--px-10);
					color: var(--neutrals-700);
					border-bottom: var(--px-1) solid var(--neutrals-200);
				}
				.manageITGCListHeading {
					font-size: var(--px-14);
					font-weight: 300;
					color: var(--neutrals-900);
					padding: var(--px-10) 0 0 0;
				}
				.manageITGCContainer {
					padding: 0 0 var(--px-10) 0;
					.manageITGCListHeading {
						font-size: var(--px-14);
						font-weight: 300;
						color: var(--neutrals-900);
					}
					.manageITGCListContainer {
						display: flex;
						align-items: center;
						padding: var(--px-10) 0;
						border-bottom: var(--px-1) solid var(--neutrals-200);
						.manageITGCListControlName {
							display: inline-block;
							width: 80%;
							padding-right: var(--px-30);
							.manageITGCListControlNameText {
								cursor: pointer;
								font-size: var(--px-14);
								font-weight: 600;
								color: var(--blue-600);
								&:hover {
									text-decoration: underline;
								}
							}
						}
						.manageITGCListControlDesginEffective {
							font-size: var(--px-14);
							font-weight: 700;
							color: var(--neutrals-900);
							width: 20%;
						}
					}
				}
				.manageITGCListAllRisks {
					font-size: var(--px-14);
					font-weight: 300;
					color: var(--neutrals-900);
					padding: var(--px-10) 0;
					border-bottom: var(--px-1) solid var(--neutrals-200);
					.noITGCPlaceholderMessageFragment {
						margin-bottom: var(--px-5);
					}
					.noITGCPlaceholderMessage {
						display: inline-flex;
						padding: 0 var(--px-1);
					}
					a {
						color: var(--blue-600);
						font-weight: 600;
						font-size: var(--px-14);
						cursor: pointer;
					}
				}
			}
			.manageITSPContentListWrapper {
				margin-left: var(--px-10);
			}
			.manageITSPListHeading {
				font-size: var(--px-14);
				font-weight: 300;
				color: var(--neutrals-900);
				padding: var(--px-10) 0 0 0;
			}
			.manageITSPContainer {
				padding: var(--px-10) 0 0 0;
				.manageITSPListHeading {
					font-size: var(--px-14);
					font-weight: 300;
					color: var(--neutrals-900);
				}
				.manageITSPControlListContainer {
					display: flex;
					align-items: center;
					padding: var(--px-10) 0;
					border-bottom: var(--px-1) solid var(--neutrals-200);
					position: relative;
					.greyOutAreaOverLay {
						background-color: var(--neutrals-25);
						opacity: 0.7;
						position: absolute;
						width: 100%;
						height: 100%;
					}
					.manageITSPControlContainer {
						width: calc(100% - var(--px-90));
						display: flex;
						align-items: center;
					}
					.manageITSPListControlRemoveLink {
						font-weight: bold;
						font-size: var(--px-14);
						margin: 0 var(--px-10) 0 auto;
						color: var(--blue-600);
						&:hover {
							text-decoration: underline;
						}
					}
					.manageITSPListControlName {
						display: inline-block;
						width: calc(100% - var(--px-80));
						padding: 0 var(--px-30) 0 0;
						.manageITSPListControlNameText {
							color: var(--blue-600);
							cursor: pointer;
							font-size: var(--px-14);
							font-weight: 600;
							&:hover {
								text-decoration: underline;
							}
						}
					}
					.testing-label {
						font-size: var(--px-12);
						font-weight: bold;
						color: var(--neutrals-900);
						margin-left: var(--px-5);
					}
					.manageITSPListControlDesginEffective {
						font-size: var(--px-14);
						font-weight: 700;
						color: var(--neutrals-900);
						width: var(--px-190);
					}
					.motif-toggle-switch-wrapper {
						.motif-toggle-switch-on-text {
							margin-left: var(--px-5);
						}
					}
				}
			}
			.manageITSPListAllRisks {
				font-size: var(--px-14);
				font-weight: 300;
				color: var(--neutrals-900);
				padding: var(--px-10) 0;
				border-bottom: var(--px-1) solid var(--neutrals-200);
				.noITGCPlaceholderMessage {
					.noITGCPlaceholderMessageSection {
						display: inline-flex;
						padding: 0 var(--px-1);
					}
					.noITGCPlaceholderMessageSpan {
						line-height: var(--px-20);
					}
				}
				a {
					color: var(--blue-600);
					font-weight: 600;
					font-size: var(--px-14);
					cursor: pointer;
				}
			}
			.itRiskLinkWrapper {
				display: flex;
				align-items: center;
				width: 100%;
				padding: var(--px-10) 0 var(--px-10) var(--px-10);
				border-bottom: var(--px-1) solid var(--neutrals-200);
				.clickable-link {
					margin-right: var(--px-30);
				}
			}
		}
	}
`;

export default ISA315ManageITSPListItemExpanded;
